import { ApolloClient, gql } from '@apollo/client';
import { useCallback, useRef } from 'react';
import { SUBMIT_TIMEOUT_MS } from '../constants';
import { Rating, TriggerPoint, WrongPartTag } from '../types';

/**
 * The **only** writer in this feature, on both Trigger_Points and both branches of `r`.
 *
 * The `r=up` ticket branch reaches it exactly as `r=down` does — through the shared form's Submit — so
 * there is no second caller and no second mutation. An untouched `r=up` form sends `rating: Yes`, no
 * free text and no tags, and inherits the timeout, the no-identifier-is-failure rule and the
 * no-automatic-retry rule unchanged.
 */

export interface SubmitFeedbackVars {
  triggerPoint: TriggerPoint;
  rating: Rating;
  whatShouldItHaveSaid?: string;
  whatDidWeGetWrong?: string;
  wrongPartTags?: WrongPartTag[];
  formOpenId: string;
  domainIdentifier: string;
  sessionId: string;
  eventId?: string;
  messageRole?: string;
  ticketId?: string;
  deepLinkVersion?: string;
}

export interface MerlinFeedbackResult {
  feedbackRecordId: string;
  reviewBuckets: string[];
}

export const SUBMIT_MERLIN_FEEDBACK = gql`
  mutation SubmitMerlinFeedback(
    $triggerPoint: String!
    $rating: String!
    $whatShouldItHaveSaid: String
    $whatDidWeGetWrong: String
    $wrongPartTags: [String!]
    $formOpenId: String!
    $domainIdentifier: String!
    $sessionId: String!
    $eventId: String
    $messageRole: String
    $ticketId: String
    $deepLinkVersion: String
  ) {
    submitMerlinFeedback(
      triggerPoint: $triggerPoint
      rating: $rating
      whatShouldItHaveSaid: $whatShouldItHaveSaid
      whatDidWeGetWrong: $whatDidWeGetWrong
      wrongPartTags: $wrongPartTags
      formOpenId: $formOpenId
      domainIdentifier: $domainIdentifier
      sessionId: $sessionId
      eventId: $eventId
      messageRole: $messageRole
      ticketId: $ticketId
      deepLinkVersion: $deepLinkVersion
    ) {
      feedbackRecordId
      reviewBuckets
    }
  }
`;

export class SubmitTimeoutError extends Error {
  constructor() {
    super(`No response within ${SUBMIT_TIMEOUT_MS}ms — timeout`);
    this.name = 'SubmitTimeoutError';
    Object.setPrototypeOf(this, SubmitTimeoutError.prototype);
  }
}

/**
 * Reject after the budget **without aborting the request**.
 *
 * The write may still land, which is exactly why the form's message says retrying is safe rather than
 * claiming either outcome. Aborting would not make the outcome knowable — the request may already have
 * reached the store — it would only destroy the chance of the response arriving late and being useful.
 */
function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new SubmitTimeoutError()), ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        clearTimeout(timer);
        reject(error);
      }
    );
  });
}

export async function submitMerlinFeedback(
  client: ApolloClient<object>,
  variables: SubmitFeedbackVars
): Promise<MerlinFeedbackResult> {
  const { data, errors } = await withTimeout(
    client.mutate<
      { submitMerlinFeedback: MerlinFeedbackResult },
      SubmitFeedbackVars
    >({
      mutation: SUBMIT_MERLIN_FEEDBACK,
      variables,
      errorPolicy: 'all',
    }),
    SUBMIT_TIMEOUT_MS
  );

  if (errors && errors.length > 0) {
    throw new Error(errors[0].message || 'Failed to submit feedback');
  }

  const result = data?.submitMerlinFeedback;
  // A response carrying no identifier is a failure, not a success. Treating it as success would tell the
  // user their feedback was recorded when nothing may have been written.
  if (
    !result ||
    !result.feedbackRecordId ||
    result.feedbackRecordId.length < 1
  ) {
    throw new Error('The response carried no feedback record identifier');
  }
  return result;
}

export interface UseSubmitFeedback {
  /** Stable for the life of this form open. Diagnostic only; never part of a target reference. */
  formOpenId: string;
  submit: (
    draft: Omit<SubmitFeedbackVars, 'formOpenId'>
  ) => Promise<MerlinFeedbackResult>;
}

/**
 * Holds one `formOpenId` for the life of the open and sends it on every attempt.
 *
 * **Mints no idempotency token, deliberately.** A retry sends the same target and the same content, so
 * it composes the same store key and lands on the same record — idempotency is structural here, and a
 * token would be inert. Never retries automatically: a retry happens only on an explicit re-activation
 * of Submit.
 */
export function useSubmitFeedback(
  client: ApolloClient<object>,
  makeFormOpenId: () => string = defaultFormOpenId
): UseSubmitFeedback {
  const formOpenIdRef = useRef<string | null>(null);
  if (formOpenIdRef.current === null) formOpenIdRef.current = makeFormOpenId();
  const formOpenId = formOpenIdRef.current;

  const submit = useCallback(
    (draft: Omit<SubmitFeedbackVars, 'formOpenId'>) =>
      submitMerlinFeedback(client, { ...draft, formOpenId }),
    [client, formOpenId]
  );

  return { formOpenId, submit };
}

function defaultFormOpenId(): string {
  return `open-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}
